import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { Box, Button, Container, IconButton, Paper, Stack, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  TypeCategories,
  clearScrollReference,
  openGroup,
  saveFramework,
  selectControlCount,
  selectImplementationStatus,
  selectScrollReferenceId,
  selectSelectedFrameworkId,
  selectSelectedGroupControls,
  setScrollPosition,
} from '../../redux/reducers/frameworkReducer';
import debounce from '../../utils/debounce';
import ManagementControl from './ManagementControl';
import ManagementControlList from './ManagementControlList';

function ImplementationStatusIndicators() {
  const { t } = useTranslation();
  const controlCount = useSelector((state) => selectControlCount(state, TypeCategories.MANAGEMENT));
  const implementationCount = useSelector((state) => selectImplementationStatus(state, TypeCategories.MANAGEMENT));
  return (
    <Box sx={{ mt: '16px', mb: '24px' }}>
      <Stack justifyContent="space-evenly" direction="row" spacing={2}>
        <Paper variant="elevation" subtype="completion" align="center" sx={{ width: '100%' }}>
          <Stack direction="column">
            <Typography variant="overline">{t('framework.controls.statusCount.notImplemented')}</Typography>
            <Typography variant="overline">{implementationCount.notImplemented}</Typography>
            <Typography variant="overline">
              {`(${Math.round((implementationCount.notImplemented / controlCount) * 1000) / 10}%)`}
            </Typography>
          </Stack>
        </Paper>
        <Paper variant="elevation" subtype="completion" align="center" sx={{ width: '100%' }}>
          <Stack direction="column">
            <Typography variant="overline">{t('framework.controls.statusCount.partiallyImplemented')}</Typography>
            <Typography variant="overline">{implementationCount.partiallyImplemented}</Typography>
            <Typography variant="overline">
              {`(${Math.round((implementationCount.partiallyImplemented / controlCount) * 1000) / 10}%)`}
            </Typography>
          </Stack>
        </Paper>
        <Paper variant="elevation" subtype="completion" align="center" sx={{ width: '100%' }}>
          <Stack direction="column">
            <Typography variant="overline">{t('framework.controls.statusCount.fullyImplemented')}</Typography>
            <Typography variant="overline">{implementationCount.fullyImplemented}</Typography>
            <Typography variant="overline">
              {`(${Math.round((implementationCount.fullyImplemented / controlCount) * 1000) / 10}%)`}
            </Typography>
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
}

function ManagementControls() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const frameworkId = useSelector(selectSelectedFrameworkId);
  const controlList = useSelector((state) => selectSelectedGroupControls(state, TypeCategories.MANAGEMENT));
  const [editMode, setEditMode] = useState(false);
  const [showTitles, setShowTitles] = useState(true);
  const scrollReference = useRef(null);
  const scrollReferenceId = useSelector(selectScrollReferenceId);

  useEffect(() => {
    if (scrollReferenceId !== null) {
      scrollReference?.current?.scrollIntoView();
      dispatch(clearScrollReference());
      window.scrollTo({ top: 0 });
    }
  }, [scrollReferenceId]);

  useEffect(() => {
    dispatch(openGroup(TypeCategories.MANAGEMENT));
  }, []);

  const toggleEditMode = () => {
    if (editMode) {
      dispatch(saveFramework(frameworkId));
    }
    setEditMode(!editMode);
  };

  const toggleTitles = () => {
    setShowTitles(!showTitles);
  };

  const handleScroll = (e) => {
    dispatch(setScrollPosition(e.target.scrollTop));
  };
  const debouncedHandleScroll = debounce(handleScroll, 100);

  return (
    <Container variant="controlBaseView" disableGutters maxWidth={false}>
      {controlList.length === 0 ? (
        <Stack alignItems="center" sx={{ mt: '40px' }}>
          <Typography variant="noActivities">{t('framework.management.noControlsSelected')}</Typography>
        </Stack>
      ) : (
        <>
          <Box sx={{ maxWidth: '1200px' }}>
            <ImplementationStatusIndicators />
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="tabHeader" sx={{ ml: '16px' }}>
                {t('framework.tabs.management')}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={toggleEditMode}
                endIcon={editMode ? <CheckIcon /> : <EditIcon />}
              >
                {editMode ? t('framework.controls.exitEditMode') : t('framework.controls.enterEditMode')}
              </Button>
            </Stack>
          </Box>
          <Container variant="controlDataView" maxWidth={false} disableGutters>
            <Box
              sx={{
                p: '2px',
                mr: '16px',
                width: showTitles ? 400 : 44,
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
            >
              <Paper variant="elevation" sx={{ minHeight: '100%' }} onClick={!showTitles ? () => toggleTitles() : null}>
                <Stack direction="row" justifyContent={showTitles ? 'flex-end' : 'center'}>
                  <IconButton onClick={showTitles ? () => toggleTitles() : null}>
                    {showTitles ? <KeyboardDoubleArrowLeftIcon /> : <KeyboardDoubleArrowRightIcon />}
                  </IconButton>
                </Stack>
                {showTitles && <ManagementControlList />}
              </Paper>
            </Box>
            <Box
              onScroll={debouncedHandleScroll}
              sx={{
                p: '2px',
                flexGrow: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                maxWidth: showTitles ? 800 : 1140,
              }}
            >
              <Stack spacing={2} sx={{ width: '100%', marginBottom: '24px' }}>
                {controlList.map((control) => (
                  <ManagementControl
                    control={control}
                    key={control.id}
                    editMode={editMode}
                    scrollReference={scrollReference}
                  />
                ))}
              </Stack>
            </Box>
          </Container>
        </>
      )}
    </Container>
  );
}

export default ManagementControls;
